/*
 * Printable read-only report of an examinee's free-text (sentence-completion)
 * responses for a record. Renders a "Print Responses" button on the examiner's
 * test-history record panel; hidden when the record has no text items.
 */

import React, { useRef } from 'react';
import { Button } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useReactToPrint } from 'react-to-print';
import { slateToPlainText } from '../../utils/slate-plain-text';
import { textItemsOf, completionFor } from '../../utils/questionnaire-responses';

const reportStyles = {
  container: { padding: '24px', color: '#000', fontFamily: 'serif' },
  header: { marginBottom: '24px', borderBottom: '1px solid #000', paddingBottom: '12px' },
  headerRow: { fontSize: '14px', marginBottom: '4px' },
  list: { listStyleType: 'decimal', paddingLeft: '24px' },
  listItem: { marginBottom: '16px', fontSize: '14px' },
  stem: { fontWeight: 'bold' },
  completion: { whiteSpace: 'pre-wrap', marginTop: '4px' }
};

const PrintResponses = ({ record, examinee }) => {
  const printRef = useRef(null);

  const childName =
    examinee?.firstName || examinee?.lastName
      ? `${examinee.firstName || ''} ${examinee.lastName || ''}`.trim()
      : '';
  const raterName = record?.data?.respondent_name || record?.data?.rater_fullName || '';
  const testDate = record?.testDate ? new Date(record.testDate).toDateString() : '';
  const testName = record?.test?.name || '';

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${testName} - ${childName} - Responses`,
    pageStyle: '@page { margin: 12mm; }',
    removeAfterPrint: true
  });

  const textItems = textItemsOf(record);
  if (textItems.length === 0) return null;

  return (
    <>
      <Button
        icon={<PrinterOutlined />}
        onClick={handlePrint}
        data-testid={`print-responses-${record._id}`}
      >
        Print Responses
      </Button>

      <div style={{ display: 'none' }}>
        <div ref={printRef} style={reportStyles.container}>
          <div style={reportStyles.header}>
            <div style={reportStyles.headerRow}>
              <strong>{testName}</strong>
            </div>
            <div style={reportStyles.headerRow}>
              Examinee: <span>{childName}</span>
            </div>
            <div style={reportStyles.headerRow}>
              Rater: <span>{raterName}</span>
            </div>
            <div style={reportStyles.headerRow}>{testDate}</div>
          </div>

          <ol style={reportStyles.list}>
            {textItems.map((item) => {
              const stem = slateToPlainText(item.content?.examinee?.document) || item.label || '';
              const completion = completionFor(record, item);
              return (
                <li key={item._id} style={reportStyles.listItem}>
                  <div style={reportStyles.stem}>{stem}</div>
                  <div style={reportStyles.completion}>{completion || '—'}</div>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </>
  );
};

export default PrintResponses;
